import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Notice } from '../components/Notice';
import { ROLE_LABELS, type Role } from '../domain/roles';
import { useSession } from './SessionContext';
import type { ReactNode } from 'react';

/**
 * Une route sans session valide renvoie vers la page de connexion. Ce garde
 * ne protège rien par lui-même : le serveur refuse de toute façon.
 *
 * Sans enfant, il rend l'`Outlet` de la route. Avec, il rend ce qu'on lui
 * donne — ce qui permet d'enchâsser sous lui ce qui exige une session, comme
 * le canal en direct, sans avoir à répéter la garde.
 */
export function RequireSession({ children }: { children?: ReactNode }) {
  const { status } = useSession();
  const location = useLocation();

  if (status === 'chargement') {
    return <div className="chargement">Chargement de la session…</div>;
  }
  if (status === 'anonyme') {
    return <Navigate to="/connexion" replace state={{ from: location.pathname }} />;
  }
  return <>{children ?? <Outlet />}</>;
}

export function RequireRole({
  role,
  allowed,
  children,
}: {
  role: Role;
  allowed: (current: Role) => boolean;
  children: ReactNode;
}) {
  const { user } = useSession();
  if (user === null || !allowed(user.role)) {
    return (
      <Notice tone="erreur" title="Accès refusé">
        Cet écran est réservé au rôle {ROLE_LABELS[role]}.
      </Notice>
    );
  }
  return <>{children}</>;
}
